const TCP_URL_PREFIX_REGEX = /^tcp:\/\//i;
const SCHEME_SPLIT_REGEX = /^(https?|tcp):\/\/(.*)$/i;
const ANY_SCHEME_SPLIT_REGEX = /^[a-z][a-z0-9+.-]*:\/\//i;

const RELAY_ENDPOINT_SCHEMES = [ 'https', 'http', 'tcp' ] as const;
type RelayEndpointScheme = typeof RELAY_ENDPOINT_SCHEMES[number];

const DEFAULT_RELAY_ENDPOINT_SCHEME: RelayEndpointScheme = 'https';

export const isTcpEndpointUrl = (url: string | undefined | null): boolean =>
    TCP_URL_PREFIX_REGEX.test(url ?? '');

export const parseEndpointUrl = (url: string | undefined | null): { scheme: RelayEndpointScheme; host: string } => {
    const value = (url ?? '').trim();
    const match = value.match(SCHEME_SPLIT_REGEX);
    if (!match) {
        return {
            scheme: DEFAULT_RELAY_ENDPOINT_SCHEME,
            host: value,
        };
    }
    return {
        scheme: match[1].toLowerCase() as RelayEndpointScheme,
        host: match[2].trim(),
    };
};

export const buildEndpointUrl = (scheme: string, host: string): string =>
    `${scheme}://${parseEndpointUrl(host).host}`;

export const isValidEndpointHost = (host: string | undefined | null): boolean => {
    const value = (host ?? '').trim();

    // A blank host is never valid.
    if (!value) {
        return false;
    }

    // The host field accepts either a bare host (e.g. "example.com") or a full
    // URL whose scheme is one we support (https/http/tcp) — the latter gets its
    // prefix split off into the scheme dropdown. Reject a value that carries an
    // unsupported scheme prefix (e.g. "ftp://..."): it looks like a URL
    // (ANY_SCHEME_SPLIT_REGEX matches) but is not one of ours (SCHEME_SPLIT_REGEX
    // does not).
    if (ANY_SCHEME_SPLIT_REGEX.test(value) && !SCHEME_SPLIT_REGEX.test(value)) {
        return false;
    }

    // Finally, ensure something remains after stripping any supported scheme
    // prefix — e.g. "https://" alone parses to an empty host and is invalid.
    const { host: parsedHost } = parseEndpointUrl(value);

    return !!parsedHost;
};
